import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { usePageContent, getSection } from '@/hooks/usePageContent';
import { useNearestOffice } from '@/hooks/useNearestOffice';
import SEO from '@/components/SEO';
import LeadCaptureForm from '@/components/LeadCaptureForm';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  MessageCircle,
  Send,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Building2,
  Users,
  Award,
  Globe,
  Loader2,
  ExternalLink,
  Navigation,
  Star,
  LocateFixed
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";

const API_URL = window.location.origin;

// Map a /api/locations/public/{slug} document to the shape ContactPage cards expect.
// ContactPage historically used camelCase keys (googleMapsEmbed, isHeadquarters) — keep that shape.
const mapLocationDocToBranch = (loc) => {
  const addr = loc.address || {};
  const shortAddress = [addr.line1, addr.line2, addr.city].filter(Boolean).join(', ');
  const fullAddress = [addr.line1, addr.line2, addr.city, addr.state && addr.pincode ? `${addr.state} ${addr.pincode}` : (addr.state || addr.pincode)]
    .filter(Boolean)
    .join(', ');
  return {
    id: loc.slug,
    slug: loc.slug,
    city: loc.city,
    area: loc.area || undefined,
    state: loc.state,
    region: loc.region || '',
    address: shortAddress,
    fullAddress,
    phone: loc.phone,
    email: loc.email,
    whatsapp: loc.whatsapp,
    coordinates: loc.coordinates,
    googleMapsEmbed: loc.google_maps_embed || '',
    googleMapsUrl: loc.google_maps_url || '',
    rating: loc.rating,
    reviews: loc.reviews,
    color: loc.color || 'from-ux-cyan to-ux-blue',
    isHeadquarters: !!loc.is_headquarters,
  };
};


const companyStats = [
  { icon: Building2, value: '11', label: 'Offices Across India' },
  { icon: Users, value: '130+', label: 'Expert Team Members' },
  { icon: Globe, value: '30,000+', label: 'Students Sent Abroad' },
  { icon: Award, value: '4.9', label: 'Google Rating' }
];

const services = [
  'Student Profile Assessment',
  'University & Course Shortlisting',
  'SOP & LOR Writing Support',
  'University Application Process',
  'Online IELTS Mentoring',
  'Education Loan Guidance',
  'Visa Application Process',
  'Accommodation Support',
  'Scholarship Guidance',
  'Other'
];

const destinations = [
  'USA', 'UK', 'Canada', 'Australia', 'Germany', 'France', 'Ireland', 'Singapore', 'New Zealand', 'Not Decided'
];

// Custom hook to fetch live Google ratings
const useLiveRatings = () => {
  const [liveRatings, setLiveRatings] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRatings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/places/ratings/all`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.ratings) {
            // Create a map of city -> rating data
            const ratingsMap = {};
            data.ratings.forEach(r => {
              // Extract city name from the place name (e.g., "Unixperts Ahmedabad" -> "ahmedabad")
              const cityKey = r.name.toLowerCase().replace('unixperts ', '').replace('unixperts', '').trim();
              ratingsMap[cityKey] = {
                rating: r.rating,
                reviews: r.user_ratings_total,
                googleMapsUrl: r.url
              };
            });
            setLiveRatings(ratingsMap);
          }
        }
      } catch (error) {
        console.error('Error fetching live ratings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRatings();
  }, []);

  return { liveRatings, loading };
};

// Branch Card Component
const BranchCard = ({ branch, isDark, liveRating, isNearest = false }) => {
  const [showMap, setShowMap] = useState(false);
  
  // Use live rating if available, otherwise fall back to static data
  const rating = liveRating?.rating || branch.rating;
  const reviews = liveRating?.reviews || branch.reviews;
  const googleMapsUrl = liveRating?.googleMapsUrl || branch.googleMapsUrl;
  
  return (
    <div className="relative" data-testid={isNearest ? 'nearest-branch-card' : undefined}>
      {isNearest && (
        <span
          className="absolute -top-3 left-4 z-20 px-3 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-ux-cyan to-ux-blue text-white shadow-md whitespace-nowrap"
        >
          Nearest to you
        </span>
      )}
      <Card
        className={`overflow-hidden transition-all duration-300 hover:shadow-xl group ${
          isNearest
            ? (isDark ? 'ring-2 ring-ux-cyan bg-ux-cyan/5' : 'ring-2 ring-ux-blue bg-ux-blue/5')
            : (isDark ? 'bg-[#1a2830] border-white/10 hover:border-ux-cyan/30' : 'bg-white hover:border-ux-cyan/30')
        }`}>
      <div className={`h-1.5 bg-gradient-to-r ${branch.color}`} />
      
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <MapPin className={`w-4 h-4 ${isDark ? 'text-ux-cyan' : 'text-ux-blue'}`} />
              <h3 className={`font-bold text-lg font-display ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {branch.city}
                {branch.area && <span className={`font-normal text-sm ml-1 ${isDark ? 'text-white/60' : 'text-slate-500'}`}>({branch.area})</span>}
              </h3>
            </div>
            {branch.isHeadquarters && (
              <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs">
                Corporate HQ
              </Badge>
            )}
          </div>
          {rating && (
            <a 
              href={googleMapsUrl || '#'} 
              target="_blank" 
              rel="noopener noreferrer"
              className={`flex items-center gap-1 px-2 py-1 rounded-full transition-all hover:scale-105 ${isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-amber-50 hover:bg-amber-100'}`}
              title="View on Google Maps - Live Rating"
            >
              <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
              <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-amber-700'}`}>{rating}</span>
              {reviews && <span className={`text-xs ${isDark ? 'text-white/50' : 'text-amber-600'}`}>({reviews})</span>}
            </a>
          )}
        </div>

        {/* Address */}
        <p className={`text-sm mb-4 leading-relaxed ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
          {branch.address}
        </p>

        {/* Map Preview */}
        <div className={`relative mb-4 rounded-xl overflow-hidden ${isDark ? 'bg-white/5' : 'bg-slate-100'}`}>
          {showMap ? (
            <iframe
              src={branch.googleMapsEmbed}
              width="100%"
              height="160"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title={`${branch.city} office location`}
            />
          ) : (
            <div 
              className="h-40 flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => setShowMap(true)}
            >
              <div className="text-center">
                <MapPin className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-ux-cyan' : 'text-ux-blue'}`} />
                <p className={`text-sm ${isDark ? 'text-white/60' : 'text-slate-500'}`}>Click to load map</p>
              </div>
            </div>
          )}
          <a 
            href={branch.googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
              isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-white text-slate-700 hover:bg-slate-100 shadow-sm'
            }`}
          >
            <Navigation className="w-3 h-3" />
            Directions
          </a>
        </div>

        {/* Contact Info */}
        <div className="space-y-3 mb-5">
          <a 
            href={`tel:${branch.phone.replace(/\s/g, '')}`}
            className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${
              isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'
            }`}
          >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDark ? 'bg-ux-cyan/20' : 'bg-ux-cyan/10'}`}>
              <Phone className={`w-4 h-4 ${isDark ? 'text-ux-cyan' : 'text-ux-blue'}`} />
            </div>
            <div>
              <p className={`text-xs ${isDark ? 'text-white/50' : 'text-slate-400'}`}>Phone</p>
              <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>{branch.phone}</p>
            </div>
          </a>
          
          <a 
            href={`mailto:${branch.email}`}
            className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${
              isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'
            }`}
          >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDark ? 'bg-violet-500/20' : 'bg-violet-100'}`}>
              <Mail className={`w-4 h-4 ${isDark ? 'text-violet-400' : 'text-violet-600'}`} />
            </div>
            <div>
              <p className={`text-xs ${isDark ? 'text-white/50' : 'text-slate-400'}`}>Email us</p>
              <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>{branch.email}</p>
            </div>
          </a>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button 
            className={`flex-1 bg-gradient-to-r ${branch.color} text-white hover:opacity-90`}
            asChild
          >
            <a href={`tel:${branch.phone.replace(/\s/g, '')}`}>
              Avail Free Counselling
            </a>
          </Button>
          <Button
            variant="outline"
            className={`${isDark ? 'border-green-500/30 text-green-400 hover:bg-green-500/10' : 'border-green-500 text-green-600 hover:bg-green-50'}`}
            asChild
          >
            <a 
              href={`https://wa.me/${branch.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2"
            >
              <MessageCircle className="w-4 h-4" />
              Chat
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
    </div>
  );
};

export const ContactPage = () => {
  const { isDark } = useTheme();
  const [formData, setFormData] = useState({
    firstName: '', 
    lastName: '', 
    email: '', 
    phone: '', 
    studyDestination: '',
    studyArea: '',
    studyIntake: '',
    studyYear: '', 
    preferredBranch: '', 
    detectedCity: '', 
    detectedState: '',
    agreeTerms: false,
    agreeContact: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [locationError, setLocationError] = useState(null);
  
  // Fetch live Google ratings
  const { liveRatings } = useLiveRatings();
  
  // Fetch CMS content for the Contact page
  const { content: cmsContent } = usePageContent('contact');
  
  // Get CMS-managed content with fallbacks
  const heroContent = getSection(cmsContent, 'hero', {
    badge_text: 'Get in Touch',
    title: 'Let\'s Start Your',
    title_highlight: 'Journey',
    subtitle: 'Our expert counselors are ready to guide you through every step of your study abroad journey. Book a free consultation today.'
  });
  
  const seoContent = getSection(cmsContent, 'seo', {
    title: 'Contact Us',
    description: 'Contact UniXperts for free study abroad counseling. Visit our offices across India or schedule a virtual consultation with our education experts.',
    keywords: 'contact UniXperts, study abroad consultation, education counseling India, free consultation, branch offices'
  });
  
  // Fetch branch offices from /api/locations/public (single source of truth shared with /locations SEO pages)
  const [branchOffices, setBranchOffices] = useState([]);
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/locations/public`);
        if (res.ok) {
          const data = await res.json();
          setBranchOffices(Array.isArray(data) ? data.map(mapLocationDocToBranch) : []);
        }
      } catch (e) {
        console.error('Failed to load branch offices:', e);
      }
    };
    load();
  }, []);

  // Branch offices come from the DB via /api/locations/public (single source of truth
  // shared with /locations SEO pages). CMS override is no longer honoured here so that
  // /contact and /locations never drift apart.
  const branchOfficesContent = branchOffices;

  // Nearest-office resolver (browser geolocation + haversine on stored coordinates)
  const { status: geoStatus, nearest: nearestBranchGeo, distanceKm: nearestDistanceKm } =
    useNearestOffice(branchOfficesContent);
  const nearestBranchSlug = nearestBranchGeo?.slug || null;

  // When nearest office is resolved, pre-fill the region filter tab
  useEffect(() => {
    if (!nearestBranchGeo) return;
    const stateToRegion = {
      Haryana: 'north',
      Gujarat: 'west', Maharashtra: 'west',
      Karnataka: 'south', 'Tamil Nadu': 'south', Telangana: 'south', Kerala: 'south', 'Andhra Pradesh': 'south',
    };
    const region = stateToRegion[nearestBranchGeo.state];
    if (region) setSelectedRegion(region);
  }, [nearestBranchGeo]);
  
  const cmsCompanyStats = getSection(cmsContent, 'company_stats', null);
  const companyStatsContent = cmsCompanyStats && Array.isArray(cmsCompanyStats) && cmsCompanyStats.length > 0 
    ? cmsCompanyStats 
    : companyStats;

  // Study Destination options (countries)
  const studyDestinations = [
    'Australia', 'Austria', 'Canada', 'Denmark', 'Dubai', 'Finland', 'France', 
    'Germany', 'Hungary', 'Ireland', 'Italy', 'Latvia', 'Luxembourg', 'Malaysia', 
    'Malta', 'Netherlands', 'New Zealand', 'Singapore', 'Spain', 'Sweden', 
    'Switzerland', 'UK', 'USA', 'Not Decided', 'Others'
  ];

  // Study Area options
  const studyAreas = [
    'Accounting', 'Agricultural Sciences', 'Arts & Design', 'Building & Architecture',
    'Business', 'Computing & IT', 'Economics', 'Engineering', 'Environmental Science',
    'Health & Medicine', 'Hospitality & Tourism', 'Human Welfare Studies & Services',
    'Language & Culture', 'Law & Legal Studies', 'Marketing & Media Communications',
    'Performing Arts & Music', 'Political & Social Science', 'Teaching & Education',
    'Pharmacy', 'Sciences', 'Not Decided', 'Others'
  ];

  // Study Intake options (quarters)
  const studyIntakes = [
    { value: 'q1', label: 'Q1 - Jan - Mar' },
    { value: 'q2', label: 'Q2 - Apr - Jun' },
    { value: 'q3', label: 'Q3 - Jul - Sep' },
    { value: 'q4', label: 'Q4 - Oct - Dec' },
    { value: 'not_decided', label: 'Not Decided' }
  ];

  // Study year options (current year + next 3 years)
  const currentYear = new Date().getFullYear();
  const studyYears = [
    { value: `${currentYear}`, label: `${currentYear}` },
    { value: `${currentYear + 1}`, label: `${currentYear + 1}` },
    { value: `${currentYear + 2}`, label: `${currentYear + 2}` },
    { value: `${currentYear + 3}`, label: `${currentYear + 3}` },
    { value: 'not_sure', label: 'Not Decided' }
  ];

  // Map detected location to nearest branch
  // Handles states with multiple offices intelligently
  const mapLocationToBranch = (city, state) => {
    const cityLower = city?.toLowerCase()?.trim() || '';
    const stateLower = state?.toLowerCase()?.trim() || '';
    
    // Direct city matches (exact office locations)
    const cityMappings = {
      // Gujarat
      'ahmedabad': 'ahmedabad',
      // Karnataka
      'bengaluru': 'bengaluru',
      'bangalore': 'bengaluru',
      // Tamil Nadu
      'chennai': 'chennai',
      // Haryana
      'gurgaon': 'gurgaon',
      'gurugram': 'gurgaon',
      // Telangana - Hyderabad has 2 offices (Somajiguda & Kukatpally)
      'hyderabad': 'hyderabad-somajiguda', // Default to Somajiguda (main office)
      'secunderabad': 'hyderabad-somajiguda',
      'kukatpally': 'hyderabad-kukatpally',
      'miyapur': 'hyderabad-kukatpally',
      'kondapur': 'hyderabad-kukatpally',
      'hitec city': 'hyderabad-kukatpally',
      'gachibowli': 'hyderabad-kukatpally',
      'madhapur': 'hyderabad-kukatpally',
      'kphb': 'hyderabad-kukatpally',
      'jntu': 'hyderabad-kukatpally',
      'somajiguda': 'hyderabad-somajiguda',
      'begumpet': 'hyderabad-somajiguda',
      'ameerpet': 'hyderabad-somajiguda',
      'punjagutta': 'hyderabad-somajiguda',
      'banjara hills': 'hyderabad-somajiguda',
      'jubilee hills': 'hyderabad-somajiguda',
      // Kerala - 2 offices (Kochi & Kottayam)
      'kochi': 'kochi',
      'ernakulam': 'kochi',
      'cochin': 'kochi',
      'aluva': 'kochi',
      'thrissur': 'kochi',
      'palakkad': 'kochi',
      'malappuram': 'kochi',
      'calicut': 'kochi',
      'kozhikode': 'kochi',
      'kannur': 'kochi',
      'kottayam': 'kottayam',
      'pathanamthitta': 'kottayam',
      'alappuzha': 'kottayam',
      'idukki': 'kottayam',
      'kollam': 'kottayam',
      'thiruvananthapuram': 'kottayam',
      'trivandrum': 'kottayam',
      // Maharashtra
      'pune': 'pune',
      'mumbai': 'pune',
      // Andhra Pradesh - 2 offices (Vijayawada & Vizag)
      'vijayawada': 'vijayawada',
      'guntur': 'vijayawada',
      'nellore': 'vijayawada',
      'tirupati': 'vijayawada',
      'kurnool': 'vijayawada',
      'anantapur': 'vijayawada',
      'kadapa': 'vijayawada',
      'ongole': 'vijayawada',
      'eluru': 'vijayawada',
      'machilipatnam': 'vijayawada',
      'visakhapatnam': 'vizag',
      'vizag': 'vizag',
      'vizianagaram': 'vizag',
      'srikakulam': 'vizag',
      'rajahmundry': 'vizag',
      'kakinada': 'vizag',
      'bhimavaram': 'vizag'
    };
    
    if (cityMappings[cityLower]) {
      return cityMappings[cityLower];
    }
    
    // District-level matching for multi-office states
    // Check if city name contains any known area
    for (const [area, office] of Object.entries(cityMappings)) {
      if (cityLower.includes(area) || area.includes(cityLower)) {
        return office;
      }
    }
    
    // State-based mapping with intelligent defaults for multi-office states
    const stateMapping = {
      'gujarat': 'ahmedabad',
      'karnataka': 'bengaluru',
      'tamil nadu': 'chennai',
      'haryana': 'gurgaon',
      'delhi': 'gurgaon',
      'new delhi': 'gurgaon',
      'uttar pradesh': 'gurgaon',
      'punjab': 'gurgaon',
      'rajasthan': 'gurgaon',
      'himachal pradesh': 'gurgaon',
      'uttarakhand': 'gurgaon',
      'jammu and kashmir': 'gurgaon',
      // Telangana - default to main Hyderabad office (Somajiguda)
      'telangana': 'hyderabad-somajiguda',
      // Kerala - default to Kochi (larger city, more accessible)
      'kerala': 'kochi',
      // Maharashtra
      'maharashtra': 'pune',
      // Andhra Pradesh - default to Vijayawada (capital region)
      'andhra pradesh': 'vijayawada',
      // Nearby states
      'odisha': 'vizag',
      'west bengal': 'vizag',
      'chhattisgarh': 'hyderabad-somajiguda',
      'madhya pradesh': 'pune',
      'goa': 'pune',
      'bihar': 'gurgaon',
      'jharkhand': 'vizag',
      'assam': 'gurgaon',
      'northeast': 'gurgaon'
    };
    
    return stateMapping[stateLower] || 'gurgaon'; // Default to Gurgaon (HQ)
  };

  // Detect user location
  const detectLocation = () => {
    setIsDetectingLocation(true);
    setLocationError(null);
    
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      setIsDetectingLocation(false);
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          // Use reverse geocoding to get city/state
          const { latitude, longitude } = position.coords;
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`
          );
          
          if (response.ok) {
            const data = await response.json();
            const city = data.address?.city || data.address?.town || data.address?.village || data.address?.county || '';
            const state = data.address?.state || '';
            
            // Map to nearest branch
            const nearestBranch = mapLocationToBranch(city, state);
            
            setFormData(prev => ({
              ...prev,
              preferredBranch: nearestBranch,
              detectedCity: city,
              detectedState: state
            }));
          }
        } catch (error) {
          console.error('Geocoding error:', error);
          setLocationError('Could not determine your location');
        }
        setIsDetectingLocation(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        setLocationError(error.code === 1 ? 'Location access denied' : 'Could not get your location');
        setIsDetectingLocation(false);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  };

  // Auto-detect location on component mount
  useEffect(() => {
    // Only auto-detect if branch not already selected
    if (!formData.preferredBranch) {
      detectLocation();
    }
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const res = await fetch(`${API_URL}/api/public/contact/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        setIsSubmitted(true);
        // Track successful contact form submission
        if (typeof window.plausible === 'function') {
          window.plausible('Contact Form Submit', { props: { inquiry: formData.inquiry_type || 'general' } });
        }
      }
    } catch (error) {
      console.error('Submission error:', error);
    }
    
    setIsSubmitting(false);
  };

  // Filter branches by region
  const filteredBranches = selectedRegion === 'all' 
    ? branchOfficesContent 
    : branchOfficesContent.filter(b => {
        if (selectedRegion === 'north') return ['Haryana'].includes(b.state);
        if (selectedRegion === 'south') return ['Karnataka', 'Tamil Nadu', 'Telangana', 'Kerala', 'Andhra Pradesh'].includes(b.state);
        if (selectedRegion === 'west') return ['Gujarat', 'Maharashtra'].includes(b.state);
        return true;
      });

  return (
    <>
      <SEO 
        title={seoContent.title || "Contact UniXperts - 11 Offices Across India | Study Abroad Consultants"}
        description={seoContent.description || "Contact UniXperts study abroad consultants. 11 offices in Ahmedabad, Bangalore, Chennai, Hyderabad, Gurgaon, Pune, Kochi, Kottayam, Vijayawada, Vizag. Call +91-124-685-4444 for free counseling."}
        keywords={seoContent.keywords || "UniXperts contact, study abroad consultants near me, overseas education consultants Ahmedabad, study abroad Bangalore, education consultants Chennai, UniXperts Hyderabad, UniXperts Pune, UniXperts Kochi"}
        canonical="https://unixperts.com/contact"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "UniXperts Education Consultancy",
          "description": "India's leading study abroad consultancy with 11 offices across India",
          "url": "https://unixperts.com",
          "telephone": "+91-124-685-4444",
          "email": "info@unixperts.com",
          "image": "https://unixperts.com/logo512.png",
          "priceRange": "Free Consultation",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "1st Floor, Plot No. 114, Sector 44",
            "addressLocality": "Gurgaon",
            "addressRegion": "Haryana",
            "postalCode": "122003",
            "addressCountry": "IN"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "28.4539",
            "longitude": "77.0743"
          },
          // aggregateRating removed intentionally — Google structured-data
          // policy requires this to reflect real, verifiable reviews. We now
          // render per-office ratings live from the Google Places API (see
          // the `reviews` field wired through `liveRating` above); the site-
          // wide aggregate isn't sourced from a reviewable pipeline yet.
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
            "opens": "10:00",
            "closes": "19:00"
          }
        }}
      />
      <div className={`min-h-screen ${isDark ? 'bg-[#0d1117]' : 'bg-slate-50'}`}>
      {/* Hero Section */}
      <section className={`relative pt-28 pb-16 overflow-hidden ${isDark ? 'bg-gradient-to-b from-[#1a2830] to-[#0d1117]' : 'bg-gradient-to-b from-white to-slate-50'}`}>
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className={`absolute -top-24 -right-24 w-96 h-96 rounded-full blur-3xl ${isDark ? 'bg-ux-cyan/10' : 'bg-ux-cyan/20'}`} />
          <div className={`absolute -bottom-24 -left-24 w-96 h-96 rounded-full blur-3xl ${isDark ? 'bg-ux-violet/10' : 'bg-ux-violet/20'}`} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <Badge className="mb-4 bg-gradient-to-r from-ux-cyan to-ux-blue text-white px-4 py-1">
              <Sparkles className="w-3 h-3 mr-1" />
              {heroContent.badge_text}
            </Badge>
            <h1 className={`text-4xl md:text-5xl lg:text-6xl font-bold font-display mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {heroContent.title} <span className="gradient-text-electric">{heroContent.title_highlight}</span>
            </h1>
            <p className={`text-lg md:text-xl ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
              {heroContent.subtitle}
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
            {companyStatsContent.map((stat, i) => {
              // Handle both CMS format (icon as string) and hardcoded format (icon as component)
              const IconComponent = typeof stat.icon === 'string' 
                ? { Building2, Users, Globe, Award }[stat.icon] || Building2
                : stat.icon || Building2;
              return (
              <div 
                key={i}
                className={`text-center p-4 rounded-2xl ${isDark ? 'bg-white/5' : 'bg-white shadow-sm'}`}
              >
                <IconComponent className={`w-6 h-6 mx-auto mb-2 ${isDark ? 'text-ux-cyan' : 'text-ux-blue'}`} />
                <p className={`text-2xl font-bold font-display ${isDark ? 'text-white' : 'text-slate-900'}`}>{stat.value}</p>
                <p className={`text-sm ${isDark ? 'text-white/60' : 'text-slate-500'}`}>{stat.label}</p>
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Branch Offices Section */}
      <section className={`py-16 ${isDark ? 'bg-[#0d1117]' : 'bg-slate-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Nearest Office Banner */}
          {nearestBranchGeo && geoStatus === 'ready' && (
            <div
              className={`mb-8 rounded-2xl border p-5 md:p-6 ${
                isDark
                  ? 'bg-gradient-to-r from-ux-cyan/10 via-ux-blue/10 to-transparent border-ux-cyan/30'
                  : 'bg-gradient-to-r from-ux-cyan/5 via-ux-blue/5 to-transparent border-ux-blue/20'
              }`}
              data-testid="nearest-office-banner"
            >
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className={`p-3 rounded-xl ${isDark ? 'bg-ux-cyan/20' : 'bg-ux-blue/10'}`}>
                  <MapPin className={`w-6 h-6 ${isDark ? 'text-ux-cyan' : 'text-ux-blue'}`} />
                </div>
                <div className="flex-1">
                  <p className={`text-sm ${isDark ? 'text-white/60' : 'text-slate-500'}`}>Your nearest UniXperts office</p>
                  <h3 className={`text-lg md:text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {nearestBranchGeo.city}{nearestBranchGeo.area ? ` (${nearestBranchGeo.area})` : ''}
                    {nearestDistanceKm !== null && (
                      <span className={`ml-3 text-sm font-normal ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                        · about {nearestDistanceKm} km away
                      </span>
                    )}
                  </h3>
                </div>
                <div className="flex gap-2">
                  <Button asChild size="sm" variant="outline" className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}>
                    <a href={`tel:${(nearestBranchGeo.phone || '').replace(/\s/g, '')}`} data-testid="nearest-office-call">
                      <Phone className="w-4 h-4 mr-2" /> Call
                    </a>
                  </Button>
                  <Button asChild size="sm" className="bg-gradient-to-r from-ux-cyan to-ux-blue text-white">
                    <a href={`/locations/${nearestBranchGeo.slug}`} data-testid="nearest-office-view">
                      View Office
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          )}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h2 className={`text-2xl md:text-3xl font-bold font-display ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Our Branch Offices
              </h2>
              <p className={`${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                {branchOfficesContent.length} offices ready to serve you
              </p>
            </div>
            
            {/* Region Filter */}
            <div className="flex flex-wrap gap-2">
              {['all', 'north', 'south', 'west'].map(region => (
                <Button
                  key={region}
                  variant={selectedRegion === region ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedRegion(region)}
                  className={selectedRegion === region 
                    ? 'bg-gradient-to-r from-ux-cyan to-ux-blue text-white' 
                    : isDark ? 'border-white/20 text-white hover:bg-white/10' : ''
                  }
                >
                  {region === 'all' ? 'All Offices' : region === 'north' ? 'North India' : region === 'south' ? 'South India' : 'West India'}
                </Button>
              ))}
            </div>
          </div>

          {/* Branch Cards Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBranches.map((branch) => {
              // Match live rating by city name (handle special cases like "Hyderabad Somajiguda")
              const cityKey = branch.city.toLowerCase() + (branch.area ? ' ' + branch.area.toLowerCase() : '');
              const liveRating = liveRatings[cityKey] || liveRatings[branch.city.toLowerCase()];
              return (
                <BranchCard key={branch.id} branch={branch} isDark={isDark} liveRating={liveRating} isNearest={nearestBranchSlug === branch.slug} />
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className={`py-16 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left - Info */}
            <div>
              <Badge className={`mb-4 ${isDark ? 'bg-ux-green/20 text-ux-green' : 'bg-ux-green/10 text-ux-green'}`}>
                Free Consultation
              </Badge>
              <h2 className={`text-3xl md:text-4xl font-bold font-display mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Can't Visit? We'll Call You!
              </h2>
              <p className={`mb-8 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                Fill out the form and our counselors will get back to you within 24 hours with personalized guidance for your study abroad journey.
              </p>

              {/* Quick Contact */}
              <div className="space-y-4 mb-8">
                <a 
                  href="tel:+919639557799"
                  className={`flex items-center gap-4 p-4 rounded-xl transition-colors ${isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-slate-50 hover:bg-slate-100'}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-ux-cyan to-ux-blue flex items-center justify-center">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className={`text-sm ${isDark ? 'text-white/50' : 'text-slate-400'}`}>Toll Free</p>
                    <p className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>+91 96395 57799</p>
                  </div>
                </a>

                <a 
                  href="https://wa.me/919639557799"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-4 p-4 rounded-xl transition-colors ${isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-slate-50 hover:bg-slate-100'}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className={`text-sm ${isDark ? 'text-white/50' : 'text-slate-400'}`}>WhatsApp</p>
                    <p className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Chat with us instantly</p>
                  </div>
                </a>

                <a 
                  href="mailto:info@unixperts.com"
                  className={`flex items-center gap-4 p-4 rounded-xl transition-colors ${isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-slate-50 hover:bg-slate-100'}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className={`text-sm ${isDark ? 'text-white/50' : 'text-slate-400'}`}>Email</p>
                    <p className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>info@unixperts.com</p>
                  </div>
                </a>
              </div>

              {/* Business Hours */}
              <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-slate-50'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Clock className={`w-4 h-4 ${isDark ? 'text-ux-cyan' : 'text-ux-blue'}`} />
                  <p className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Business Hours</p>
                </div>
                <p className={`text-sm ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
                  Monday - Saturday: 10:00 AM - 7:00 PM<br />
                  Sunday: By Appointment Only
                </p>
              </div>
            </div>

            {/* Right - Form */}
            <LeadCaptureForm 
              onSuccess={() => setIsSubmitted(true)} 
              className="lg:sticky lg:top-24"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-16 ${isDark ? 'bg-gradient-to-r from-[#1a2830] to-[#0d1117]' : 'bg-gradient-to-r from-slate-900 to-slate-800'}`}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold font-display text-white mb-4">
            Ready to Start Your Journey?
          </h2>
          <p className="text-white/70 mb-8">
            Join 30,000+ students who've achieved their study abroad dreams with UniXperts
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button size="lg" className="bg-gradient-to-r from-ux-cyan to-ux-blue text-white rounded-full px-8" asChild>
              <a href="#contact-form">
                Book Free Consultation
                <ArrowRight className="w-4 h-4 ml-2" />
              </a>
            </Button>
            <Button size="lg" variant="hero-outline" className="rounded-full px-8" asChild>
              <a href="https://wa.me/919639557799" target="_blank" rel="noopener noreferrer">
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp Us
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default ContactPage;
